import { AutomatedIndexingTask } from 'features/automatedIndexing/types';
import { TABLE_MENU_ACCESSOR as GP_TABLE_MENU_ACCESSOR } from 'features/generalPayables/filesAndLogs/config';
import {
  GeneralPayablesScenario,
  GeneralPayablesTaskBase,
  GeneralPayablesTaskToRetry,
} from 'features/generalPayables/types';
import { TABLE_MENU_ACCESSOR as MP_TABLE_MENU_ACCESSOR } from 'features/maintenancePayables/filesAndLogs/config';
import {
  MaintenancePayablesScenario,
  MaintenancePayablesTask,
  MaintenancePayablesTaskBase,
} from 'features/maintenancePayables/types';
import { TABLE_MENU_ACCESSOR as OE_TABLE_MENU_ACCESSOR } from 'features/orderEntry/filesAndLogs/config';
import {
  OrderEntryScenario,
  OrderEntryTask,
  OrderEntryTaskBase,
  OrderEntryTaskToRetry,
} from 'features/orderEntry/types';
import { Task } from 'features/shared/task/types';
import { TABLE_MENU_ACCESSOR as AI_TABLE_MENU_ACCESSOR } from 'pages/automatedIndexingDetails/config';
import { MenuItem } from 'types/table';

interface BaseTaskDetailsProps<T> {
  viewFile: EmptyArgsVoidFunc;
  goToLogs: EmptyArgsVoidFunc;
  menuConfig: MenuItem<T>[];
}

interface TaskDetailsProcessTask {
  onProcessTaskClick: (task: Task) => void;
}

interface OrderEntryTaskDetailsProps
  extends BaseTaskDetailsProps<OrderEntryTaskToRetry>, TaskDetailsProcessTask {
  task: OrderEntryTaskBase;
  handleMenuItemClick: (
    accessor: OE_TABLE_MENU_ACCESSOR,
    item: OrderEntryTask
  ) => void;
}

interface OrderEntryRetryTaskDetailsProps extends BaseTaskDetailsProps<OrderEntryTaskToRetry> {
  task: OrderEntryTaskToRetry;
  handleMenuItemClick: (
    accessor: OE_TABLE_MENU_ACCESSOR,
    item: OrderEntryTaskToRetry
  ) => void;
}

interface MaintenancePayablesTaskDetailsProps
  extends
    BaseTaskDetailsProps<MaintenancePayablesTask>,
    TaskDetailsProcessTask {
  task: MaintenancePayablesTaskBase;
  handleMenuItemClick: (
    accessor: MP_TABLE_MENU_ACCESSOR,
    item: MaintenancePayablesTaskBase
  ) => void;
}

interface GeneralPayablesRetryTaskDetailsProps
  extends
    BaseTaskDetailsProps<GeneralPayablesTaskToRetry>,
    TaskDetailsProcessTask {
  task: GeneralPayablesTaskToRetry;
  handleMenuItemClick: (
    accessor: GP_TABLE_MENU_ACCESSOR,
    item: GeneralPayablesTaskToRetry
  ) => void;
}

interface AutomatedIndexingTaskDetailsProps extends BaseTaskDetailsProps<AutomatedIndexingTask> {
  task: AutomatedIndexingTask;
  onResumeTrainingTaskClick: (task: AutomatedIndexingTask) => void;
  handleMenuItemClick: (
    accessor: AI_TABLE_MENU_ACCESSOR,
    item: AutomatedIndexingTask
  ) => void;
}

export type RetryTaskDetailsProps =
  OrderEntryRetryTaskDetailsProps | GeneralPayablesRetryTaskDetailsProps;

export type TaskDetailsProps =
  | OrderEntryTaskDetailsProps
  | OrderEntryRetryTaskDetailsProps
  | MaintenancePayablesTaskDetailsProps
  | GeneralPayablesRetryTaskDetailsProps
  | AutomatedIndexingTaskDetailsProps;

export function isRetryTask(
  task: Task
): task is OrderEntryTaskToRetry | GeneralPayablesTaskToRetry {
  return 'tmsPayload' in task;
}

export function isTaskThatCanBeProcessed(
  props: any
): props is TaskDetailsProcessTask {
  return 'onProcessTaskClick' in props;
}

export function isOrderEntryTask(task: Task): task is OrderEntryTaskBase {
  return 'processInstanceId' in task;
}

export function isRetryProps(
  props: TaskDetailsProps
): props is RetryTaskDetailsProps {
  return !('onProcessTaskClick' in props);
}

export function isOrderEntryOrMaintenancePayablesTask(
  task: Task
): task is
  OrderEntryTaskBase | MaintenancePayablesTaskBase | GeneralPayablesTaskBase {
  return (
    'name' in task &&
    'processId' in task &&
    'scenarioId' in task &&
    'status' in task
  );
}

export function isMaintenancePayablesTask(
  task: Task
): task is MaintenancePayablesTaskBase {
  return (
    'name' in task &&
    'processId' in task &&
    'scenarioId' in task &&
    'status' in task &&
    (task.status === 'review_ready' ||
      task.status === 'in_review' ||
      task.status === 'ready' ||
      task.status === 'failed')
  );
}

export function isGeneralPayablesTask(
  task: Task
): task is GeneralPayablesTaskBase {
  return (
    'name' in task &&
    'processId' in task &&
    'scenarioId' in task &&
    'status' in task &&
    (task.status === 'get_tables_request_ready' ||
      task.status === 'in_get_tables_request' ||
      task.status === 'in_review' ||
      task.status === 'review_ready' ||
      task.status === 'ready' ||
      task.status === 'failed')
  );
}

export function isOrderEntryScenario(
  scenario:
    | OrderEntryScenario
    | MaintenancePayablesScenario
    | GeneralPayablesScenario
    | undefined
): scenario is OrderEntryScenario {
  return !!scenario?._links && 'getTruckmateClients' in scenario._links;
}

export function isGeneralPayablesScenario(
  scenario:
    | OrderEntryScenario
    | MaintenancePayablesScenario
    | GeneralPayablesScenario
    | undefined
): scenario is GeneralPayablesScenario {
  return (
    !!scenario?._links && 'searchTruckmateFinanceGlAccounts' in scenario._links
  );
}

export function isAutomatedIndexingProps(
  props: TaskDetailsProps
): props is AutomatedIndexingTaskDetailsProps {
  return 'onResumeTrainingTaskClick' in props;
}
